import React from 'react';
import { Grid, Paper, Typography, Box } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { getContrastText } from '../utils/colorUtils';
import { useTheme } from '@mui/material';
import { formatDateForFirebase } from '../utils/dateUtils';

const MonthlyStats = React.memo(function MonthlyStats({ habits = [], month, year, customColors }) {
  const theme = useTheme();

  // Ensure habits is an array
  const safeHabits = Array.isArray(habits) ? habits : [];

  const stats = React.useMemo(() => {
    const activeHabits = safeHabits.filter(h => h && h.active !== false);
    
    let totalPerfectDays = 0;
    let habitCompletions = new Map();
    let currentStreak = new Map();
    let maxStreak = new Map();

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Initialize completion and streak counters for each active habit
    activeHabits.forEach(habit => {
      if (habit && habit.id) {
        habitCompletions.set(habit.id, 0);
        currentStreak.set(habit.id, 0);
        maxStreak.set(habit.id, 0);
      }
    });

    // For each day in the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const formattedDate = formatDateForFirebase(date).split('T')[0];
      
      // Filter habits scheduled for this day
      const scheduledHabits = activeHabits.filter(habit => {
        if (!habit || !habit.id) return false;
        const dayOfWeek = date.getDay();
        const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
        return !habit.weekdays?.length || habit.weekdays.includes(adjustedDay);
      });

      if (scheduledHabits.length > 0) {
        // Check if all habits were completed
        const allCompleted = scheduledHabits.every(habit => 
          habit && habit.completedDates?.some(completedDate => {
            const completedDateOnly = completedDate.split('T')[0];
            return completedDateOnly === formattedDate;
          })
        );

        if (allCompleted) {
          totalPerfectDays++;
        }

        // Count completions and update streaks per habit
        scheduledHabits.forEach(habit => {
          if (!habit || !habit.id) return;
          const isCompleted = habit.completedDates?.some(completedDate => {
            const completedDateOnly = completedDate.split('T')[0];
            return completedDateOnly === formattedDate;
          });
          
          if (isCompleted) {
            habitCompletions.set(habit.id, habitCompletions.get(habit.id) + 1);
            currentStreak.set(habit.id, currentStreak.get(habit.id) + 1);
            if (currentStreak.get(habit.id) > maxStreak.get(habit.id)) {
              maxStreak.set(habit.id, currentStreak.get(habit.id));
            }
          } else {
            currentStreak.set(habit.id, 0);
          }
        });
      }
    }

    // Find most and least performed habits
    let mostPerformedHabit = null;
    let leastPerformedHabit = null;
    let maxCompletions = 0;
    let minCompletions = Infinity;
    let habitWithMaxStreak = null;
    let maxStreakCount = 0;

    activeHabits.forEach(habit => {
      if (!habit || !habit.id) return;
      const completions = habitCompletions.get(habit.id);
      const streak = maxStreak.get(habit.id);
      
      if (completions > maxCompletions) {
        maxCompletions = completions;
        mostPerformedHabit = { name: habit.name || 'Sem nome', count: completions };
      }
      
      if (completions < minCompletions && completions > 0) {
        minCompletions = completions;
        leastPerformedHabit = { name: habit.name || 'Sem nome', count: completions };
      }

      if (streak > maxStreakCount) {
        maxStreakCount = streak;
        habitWithMaxStreak = { name: habit.name || 'Sem nome', streak };
      }
    });

    // Se só houver um hábito, ele será tanto o mais quanto o menos realizado
    if (activeHabits.length === 1) {
      leastPerformedHabit = mostPerformedHabit;
    }

    // Se não houver hábitos menos realizados, pega o primeiro hábito ativo
    if (!leastPerformedHabit && activeHabits.length > 0) {
      const firstHabit = activeHabits[0];
      leastPerformedHabit = { 
        name: firstHabit.name || 'Sem nome', 
        count: habitCompletions.get(firstHabit.id) || 0 
      };
    }

    return {
      perfectDays: totalPerfectDays,
      maxStreakHabit: habitWithMaxStreak || { name: 'Nenhum', streak: 0 },
      mostPerformedHabit: mostPerformedHabit || { name: 'Nenhum', count: 0 },
      leastPerformedHabit: leastPerformedHabit || { name: 'Nenhum', count: 0 }
    };
  }, [safeHabits, month, year]);

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={6} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              }
            }}
          >
            <Box
              sx={{
                backgroundColor: customColors?.primary || theme.palette.primary.main,
                borderRadius: '50%',
                p: 1,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <EmojiEventsIcon sx={{ fontSize: 32, color: getContrastText(customColors?.primary || theme.palette.primary.main) }} />
            </Box>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 'bold', textAlign: 'center' }}>
              Dias Perfeitos
            </Typography>
            <Typography variant="body1" sx={{ 
              color: customColors?.primary || theme.palette.primary.main,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              textAlign: 'center'
            }}>
              {stats.perfectDays}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              }
            }}
          >
            <Box
              sx={{
                backgroundColor: customColors?.primary || theme.palette.primary.main,
                borderRadius: '50%',
                p: 1,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <WhatshotIcon sx={{ fontSize: 32, color: getContrastText(customColors?.primary || theme.palette.primary.main) }} />
            </Box>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 'bold', textAlign: 'center' }}>
              Maior Sequência
            </Typography>
            <Typography variant="body1" sx={{ 
              color: customColors?.primary || theme.palette.primary.main,
              fontSize: { xs: '0.75rem', sm: '1rem' },
              textAlign: 'center',
              whiteSpace: 'nowrap'
            }}>
              {`${stats.maxStreakHabit.streak}x (${stats.maxStreakHabit.name})`}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              }
            }}
          >
            <Box
              sx={{
                backgroundColor: customColors?.primary || theme.palette.primary.main,
                borderRadius: '50%',
                p: 1,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 32, color: getContrastText(customColors?.primary || theme.palette.primary.main) }} />
            </Box>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 'bold', textAlign: 'center' }}>
              Mais Realizado
            </Typography>
            <Typography variant="body1" sx={{ 
              color: customColors?.primary || theme.palette.primary.main,
              fontSize: { xs: '0.75rem', sm: '1rem' },
              textAlign: 'center',
              whiteSpace: 'nowrap'
            }}>
              {`${stats.mostPerformedHabit.count}x (${stats.mostPerformedHabit.name})`}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
              }
            }}
          >
            <Box
              sx={{
                backgroundColor: customColors?.primary || theme.palette.primary.main,
                borderRadius: '50%',
                p: 1,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <TrendingDownIcon sx={{ fontSize: 32, color: getContrastText(customColors?.primary || theme.palette.primary.main) }} />
            </Box>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 'bold', textAlign: 'center' }}>
              Menos Realizado
            </Typography>
            <Typography variant="body1" sx={{ 
              color: customColors?.primary || theme.palette.primary.main,
              fontSize: { xs: '0.75rem', sm: '1rem' },
              textAlign: 'center',
              whiteSpace: 'nowrap'
            }}>
              {`${stats.leastPerformedHabit.count}x (${stats.leastPerformedHabit.name})`}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
});

export default MonthlyStats;